import { useCallback, useState, useEffect, useRef } from 'react';
import iconRefresh from '../../../icons/refresh.svg';
import axios from '../../../services/axiosAuth';
import Tooltip from '../../Utils/Tooltip';
import '../../../styles/reload-grid.scss';
import PropTypes from 'prop-types';
import Notification from '../../Utils/Notification';
import { useIntl } from 'react-intl';

const ReloadGridButton = ({ renderParenCallBack, grid }) => {
  const { formatMessage } = useIntl();
  const [reloadStarted, setReloadStarted] = useState(false);
  const [lastReloadTime, setLastReloadTime] = useState(null);
  const [afterReload, setAfterReload] = useState(true);
  const previousReloadStatus = useRef(false);
  const currentReloadStatus = useRef(false);

  const message = formatMessage(
    {
      id: reloadStarted
        ? 'We update the data on this view. The process may take from {reloadTime} to several minutes'
        : 'Reload data on this view. The process may take from {reloadTime} to several minutes.',
    },
    {
      reloadTime: null !== lastReloadTime ? lastReloadTime : 5,
    }
  );

  const refreshPage = useCallback((force = false) => {
    if ((previousReloadStatus.current && !currentReloadStatus.current) || force) {
      renderParenCallBack();
    }
  }, []);

  const checkStatus = useCallback(() => {
    axios
      .get(`/reload-grid/${grid}/status`)
      .then((res) => {
        previousReloadStatus.current = reloadStarted;
        setReloadStarted(!res.data.isCompleted || false);
        currentReloadStatus.current = !res.data.isCompleted || false;
        setLastReloadTime(res.data.reloadTime || null);
        refreshPage();
      })
      .catch(() => {
        setReloadStarted(false);
        setLastReloadTime(null);
      });
  }, [grid, refreshPage, reloadStarted]);

  const handleClick = useCallback(() => {
    setReloadStarted(true);
    axios
      .post(`/reload-grid/${grid}`)
      .then((res) => {
        setReloadStarted(!res.data.isCompleted || false);
        refreshPage(res.data.isCompleted);
      })
      .catch(() => {
        Notification({
          type: 'error',
          text: formatMessage({ id: 'Something went wrong, try again later.' }),
        }).show();
        setReloadStarted(false);
      });
  }, [grid, refreshPage]);

  const intervalCheckStatus = useCallback(() => {
    checkStatus();
  }, [checkStatus]);

  useEffect(() => {
    if (afterReload) {
      setAfterReload(false);
      checkStatus();
    }
  }, [checkStatus, afterReload]);

  useEffect(() => {
    const timeout = 60 * 1000;
    const interval = setInterval(() => {
      intervalCheckStatus();
    }, timeout);
    return () => clearInterval(interval);
  }, [intervalCheckStatus]);

  return (
    <Tooltip placement='top' message={message} hiddenIcon='true'>
      <span className={`reload-link ${reloadStarted ? 'link-disabled' : ''}`} onClick={handleClick}>
        <img className={reloadStarted ? 'spinner' : ''} src={iconRefresh} alt='search' />

        {formatMessage({
          id: reloadStarted ? 'The data is being refreshed' : 'Refresh data',
        })}
      </span>
    </Tooltip>
  );
};

ReloadGridButton.propTypes = {
  grid: PropTypes.string.isRequired,
};

export default ReloadGridButton;
